/**
 * SE-1765 T13: React hooks around the simpl_auth-proxy Lambdas.
 *
 * `useKnownRoles` loads the T6 catalog once on mount; the response is
 * small (<1 KB) and stable per-caller, so a naive fetch beats
 * introducing a cache library at this stage. Both T13 pages call this
 * to derive their axis-specific role picker without hardcoding role
 * names.
 *
 * `useScopeChoices` refetches whenever the (axis, programId) inputs
 * change — that's the T4 provider contract at
 * ``GET /api/internal/scope-choices``: person-axes REQUIRE
 * program_id and refuse to enumerate the warehouse without one, so
 * an empty programId short-circuits into "no choices to load yet"
 * rather than firing a 400.
 *
 * `useRoleScopes` refetches whenever the (roles, scopeType, programId)
 * tuple changes and exposes a `reload()` callback so the SPA can
 * force a re-read after a write without reaching into `useEffect`'s
 * deps — the coaching page uses this to render the resulting table
 * state on save without a fake toggle prop.
 */
import { useCallback, useEffect, useState } from "react";

import {
  KnownRolesResponse,
  PersonGroupCandidatesResponse,
  PersonGroupMembersResponse,
  PersonGroupsListResponse,
  RoleScopesResponse,
  ScopeChoicesResponse,
  fetchKnownRoles,
  fetchPersonGroupMemberCandidates,
  fetchPersonGroupMembers,
  fetchRoleScopes,
  fetchScopeChoices,
  listPersonGroups,
} from "./simplAuthAPI";

type LoadState<T> = {
  data: T | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
};

const initial: LoadState<never> = { data: null, status: "idle", error: null };

export const useKnownRoles = (): LoadState<KnownRolesResponse> => {
  const [state, setState] = useState<LoadState<KnownRolesResponse>>(initial);

  useEffect(() => {
    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    fetchKnownRoles()
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

/**
 * Axes that require a program_id per simpl_auth's provider contract.
 * Kept as a Set so the "does this call need a program?" check is
 * O(1) rather than a chain of `axis === ... || axis === ...`.
 * Institution / sponsor / platform pass an undefined program_id
 * through and simpl_auth ignores it, so we don't skip them here —
 * we just don't fetch until the axis is set.
 */
const AXES_REQUIRING_PROGRAM = new Set(["person_group", "person"]);

/**
 * The by-scope grants read, with a `reload()` callback and a monotonic
 * `reloadNonce` the caller can key `useEffect` on. Semantics mirror
 * `useScopeChoices`: an empty `programId` short-circuits into idle
 * (simpl_auth refuses an unbounded read at ``list_role_scopes``, so
 * firing a 400 to prove it would be pointless), an empty `roles` list
 * does too. Emits null-data / idle while gated; emits a real result
 * once both inputs arrive.
 */
export type RoleScopesLoadState = LoadState<RoleScopesResponse> & {
  reload: () => void;
};

export const useRoleScopes = (
  roles: string[],
  scopeType: "person" | "person_group" | null,
  programId: string | null,
): RoleScopesLoadState => {
  const [state, setState] = useState<LoadState<RoleScopesResponse>>(initial);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const rolesKey = roles.join(",");

  useEffect(() => {
    if (!scopeType || !programId || roles.length === 0) {
      setState(initial);
      return;
    }

    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    fetchRoleScopes({ roles, scopeType, programId })
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
    // rolesKey is a stable string derived from `roles` so the deps
    // array doesn't rerun on identical-content array re-references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesKey, scopeType, programId, nonce]);

  return { ...state, reload };
};

// =============================================================================
// SE-1883: person-groups (rotations) plane.
// =============================================================================

/**
 * The list of rotations for one program (active AND archived — archived
 * sorted last on the wire, so the SPA can filter or split as it likes).
 * `reload()` re-runs the fetch and bumps a nonce, mirroring the pattern
 * `useRoleScopes` uses for after-write refreshes.
 */
export type PersonGroupsLoadState = LoadState<PersonGroupsListResponse> & {
  reload: () => void;
};

export const usePersonGroups = (
  programId: string | null,
): PersonGroupsLoadState => {
  const [state, setState] =
    useState<LoadState<PersonGroupsListResponse>>(initial);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!programId) {
      setState(initial);
      return;
    }
    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    listPersonGroups({ programId })
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [programId, nonce]);

  return { ...state, reload };
};

/** One rotation's current active members. `reload()` re-fetches. */
export type PersonGroupMembersLoadState = LoadState<PersonGroupMembersResponse> & {
  reload: () => void;
};

export const usePersonGroupMembers = (
  groupId: string | null,
): PersonGroupMembersLoadState => {
  const [state, setState] =
    useState<LoadState<PersonGroupMembersResponse>>(initial);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!groupId) {
      setState(initial);
      return;
    }
    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    fetchPersonGroupMembers(groupId)
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [groupId, nonce]);

  return { ...state, reload };
};

/**
 * The people who could be added as members of this rotation. Refetches
 * when the group changes; `roles` is applied server-side and only ever
 * NARROWS (never widens into candidates the subsequent add would refuse).
 */
export const usePersonGroupCandidates = (
  groupId: string | null,
  roles?: string[],
): LoadState<PersonGroupCandidatesResponse> => {
  const [state, setState] =
    useState<LoadState<PersonGroupCandidatesResponse>>(initial);
  const rolesKey = (roles ?? []).join(",");

  useEffect(() => {
    if (!groupId) {
      setState(initial);
      return;
    }
    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    fetchPersonGroupMemberCandidates(groupId, { roles })
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, rolesKey]);

  return state;
};

export const useScopeChoices = (
  axis: string | null,
  programId: string | null,
): LoadState<ScopeChoicesResponse> => {
  const [state, setState] = useState<LoadState<ScopeChoicesResponse>>(initial);

  useEffect(() => {
    if (!axis) {
      setState(initial);
      return;
    }
    if (AXES_REQUIRING_PROGRAM.has(axis) && !programId) {
      setState(initial);
      return;
    }

    let cancelled = false;
    setState({ data: null, status: "loading", error: null });

    fetchScopeChoices({ axis, programId: programId ?? undefined })
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "succeeded", error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, status: "failed", error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [axis, programId]);

  return state;
};
